import { Link } from "wouter";
import { Download, FolderDown } from "lucide-react";
import { GalleryCardsSkeleton, useSlowEnoughToShow } from "../components/Skeleton";
import { useT } from "../i18n";
import { useServiceText } from "../service-text";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { dashboardApi } from "../api";
import { DeliverableFrame, PrismicMark } from "../components/Deliverables";
import type { Delivery } from "../types";
import { formatCredits, formatDate, monthLabel } from "../utils";

/**
 * Everything Prismic delivered this month, as a wall of work.
 *
 * The rest of the product answers "what did this cost". This page answers "what
 * did we get" — the question a client actually opens a portal to see answered, and
 * the one a table of credits has never been able to.
 */
export default function GalleryPage({ month }: { month: string }) {
  const [deliveries, setDeliveries] = useState<Delivery[] | null>(null);

  useEffect(() => {
    let live = true;
    setDeliveries(null);
    dashboardApi.deliveries(month)
      .then((data) => { if (live) setDeliveries(data.deliveries); })
      .catch((error) => {
        if (live) setDeliveries([]);
        toast.error(error instanceof Error ? error.message : t("error.loadWork"));
      });
    return () => { live = false; };
  }, [month]);

  const t = useT();
  const st = useServiceText();
  const showSkeleton = useSlowEnoughToShow(!deliveries);

  // Shaped like the tiles that are coming, so the grid does not jump when they land.
  if (!deliveries) return showSkeleton
    ? <div className="page-stack"><GalleryCardsSkeleton cards={6} label="Loading the delivered work" /></div>
    : null;

  // Grouped by request, because a client thinks in pieces of work, not in files.
  const byRequest = new Map<string, { id: string; title: string; serviceId: string; service: string; credits: number; completedAt: string | null; items: Delivery[] }>();
  for (const item of deliveries) {
    const group = byRequest.get(item.requestId) || {
      id: item.requestId, title: item.requestTitle, serviceId: item.serviceId, service: item.serviceName,
      credits: item.credits, completedAt: item.completedAt, items: [],
    };
    group.items.push(item);
    byRequest.set(item.requestId, group);
  }
  const groups = [...byRequest.values()];
  const totalBytes = deliveries.reduce((sum, d) => sum + d.bytes, 0);

  if (!groups.length) {
    return (
      <div className="page-stack">
        <section className="panel">
          <div className="gallery-empty">
            <PrismicMark size={46} />
            <h4>{t("work.emptyTitle", { month: monthLabel(month) })}</h4>
            <p>{t("work.emptyHint")}</p>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="page-stack">
      <section className="table-toolbar gallery-toolbar">
        <div>
          <span className="section-eyebrow">{monthLabel(month)}</span>
          <h3 className="gallery-title">
            {groups.length} piece{groups.length === 1 ? "" : "s"} of work · {deliveries.length} file{deliveries.length === 1 ? "" : "s"}
          </h3>
        </div>
        <a className="button button-primary" href={`/api/deliveries.zip?month=${month}`}>
          <FolderDown size={17} /> {t("work.downloadAll")} ({Math.max(1, Math.round(totalBytes / 1048576))} MB)
        </a>
      </section>

      <div className="gallery-grid">
        {groups.map((group) => {
          const hero = group.items.find((i) => i.mime.startsWith("image/")) || group.items[0];
          return (
            <article className="gallery-card" key={group.id}>
              {/* The card is the way in to the hand-off screen. Without this the
                  delivery page compiles and nobody can reach it. */}
              <Link href={`/delivery/${group.id}`} className="gallery-cover-link" aria-label={t("aria.openWork", { name: group.title })}>
              <DeliverableFrame
                attachment={{
                  id: hero.id, requestId: hero.requestId, kind: "deliverable",
                  filename: hero.filename, mime: hero.mime, bytes: hero.bytes,
                  isCover: true, createdAt: hero.completedAt ?? "", uploadedByName: hero.deliveredBy,
                  // A cover thumbnail, not a delivery batch — it belongs to no version.
                  version: null, url: hero.url,
                }}
              />
              </Link>
              <div className="gallery-body">
                <Link href={`/delivery/${group.id}`} className="gallery-title-link"><strong>{group.title}</strong></Link>
                <span>{st.name(group.serviceId, group.service)}</span>
                <div className="gallery-meta">
                  <span>{formatCredits(group.credits)} credits</span>
                  <span>{group.completedAt ? formatDate(group.completedAt) : t("gallery.inProgress")}</span>
                </div>
                <ul className="gallery-files">
                  {group.items.map((item) => (
                    <li key={item.id}>
                      <a href={item.url} target="_blank" rel="noreferrer" title={item.filename}>
                        <Download size={13} /> <span>{item.filename}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}
